import { createTracker, EventTypes } from 'redux-segment';

import CONF from 'config/conf.singleton';
import FLAG from 'config/flag.singleton';
import RELEASE from 'config/release.singleton';
import { USE_SEGMENT_AND_UPSCOPE } from 'config/use.conf';
import fromObjectToKeys from 'utils/converters/object/fromObjectToKeys.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isNotFn from 'utils/predicates/function/isNotFn.util';

const L = logger('SEGMENT');

const loadUpscope = (conf) => {
  L.debug('Load Upscope', conf.upscopeUse);
  if (!conf.upscopeUse) return;

  // code provided as is with all its quirks left untouched
  /* eslint-disable */
  (function (w, u, d) {
    var i = function () {
      i.c(arguments);
    };
    i.q = [];
    i.c = function (args) {
      i.q.push(args);
    };
    var l = function () {
      var s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://code.upscope.io/qPYBL4bhqo.js';
      var x = d.getElementsByTagName('script')[0];

      x.parentNode.insertBefore(s, x);
    };
    if (typeof u !== 'function') {
      w.Upscope = i;
      l();
    }
  })(window, window.Upscope, document);

  window.Upscope('init', {
    // Don't initialise here, we do updateConnection when we are logged in
  });
  /* eslint-enable */
};

const trackPageLoad = (conf) => {
  // NOTE: We don't need to call this as it's done automatically by the analytics.js script
  if (!USE_SEGMENT_AND_UPSCOPE) {
    L.warn('trackPageLoad()', 'Should not be called due to USE_SEGMENT_AND_UPSCOPE:', USE_SEGMENT_AND_UPSCOPE);
    return;
  }

  // @SEE: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/ajs-classic/#page
  const analytics = window ? window.analytics : undefined;
  if (analytics) {
    L.debug('trackPageLoad()', 'segmentWriteKey:', conf.segmentWriteKey);
    analytics.page();
  } else {
    L.warn('trackPageLoad()', 'Missing window.analytics:', analytics);
  }
};

const identify = ({ id, traits, options }) => {
  // @SEE: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/ajs-classic/#identify

  const analytics = window ? window.analytics : undefined;
  if (!analytics) {
    L.warn('identify()', 'Missing window.analytics:', analytics);
    return;
  }

  L.debug('identify()', { id, traits, options });

  analytics.identify(id, traits, options);
};

const setCompany = (data) => {
  const analytics = window ? window.analytics : undefined;
  if (!analytics) {
    L.warn('setCompany()', 'Missing window.analytics:', analytics);
    return;
  }

  const { id, name, username, createdAt } = asObject(data);
  if (!id) {
    L.warn('setCompany()', 'No id:', id);
    return;
  }
  // An id-only company gets created in Intercom with a blank name ("Unknown"), which
  // was the root cause of the unattached-users symptom. Require name at the helper too.
  if (!name) {
    L.info('setCompany()', 'Skipping, no company name yet for id:', id);
    return;
  }

  // Fall back to the already-identified Segment user when callers don't pass username.
  let resolvedUserId = username;
  if (!resolvedUserId) {
    try {
      resolvedUserId = analytics?.user?.()?.id?.() || null;
    } catch (e) {
      L.warn('setCompany()', 'Failed reading analytics.user().id():', e);
    }
  }

  if (!resolvedUserId) {
    L.info('setCompany()', 'Skipping, no userId available yet for company:', { id, name });
    return;
  }

  // createdAt (Unix seconds) maps to Intercom's company `remote_created_at` field.
  // Only forward when callers explicitly supply it (eg. at salon-creation time) so we
  // never overwrite an existing value with a wrong one on later logins.
  const company = { id, name, ...(createdAt && { createdAt }) };
  const traits = { company };
  const options = { integrations: { All: false, Intercom: true } };

  L.debug('setCompany()', { resolvedUserId, company, options });
  analytics.identify(resolvedUserId, traits, options);
};

const upscopeUpdateConnection = (options) => {
  const FTAG = 'upscopeUpdateConnection()';

  if (!CONF.upscopeUse) return void L.debug(FTAG, 'Not enabled CONF.upscopeUse:', CONF.upscopeUse);
  if (isNotFn(window.Upscope)) return void L.warn(FTAG, 'Not a function window.Upscope:', window.Upscope);

  return window.Upscope('updateConnection', { identities: options?.identities, uniqueId: options?.uniqueId });
};

const trackLocationChange = (getState, action) => ({
  eventType: EventTypes.page,
  eventPayload: {
    name: action.payload.location.pathname,
    // Disable Intercom: pre-identify page events create anonymous Lead duplicates.
    options: { integrations: { Intercom: false } },
  },
});

const trackPostSalonDataSuccess = (getState, action) => {
  const { name, id } = action?.response ?? {};
  // Require BOTH id and name: an id-only call would create a nameless ("Unknown")
  // company in Intercom, which was the root cause of the unattached-users symptom.
  if (!id || !name) return;

  // POST_SALON_DATA_SUCCESS fires the moment the salon is created server-side, so
  // 'now' is the correct value for Intercom's Company created at (remote_created_at).
  // Unix seconds is Intercom's expected format for this field.
  const createdAt = Math.floor(Date.now() / 1000);
  SEGMENT.setCompany({ id, name, createdAt });
};

const trackCognitoLogout = () => {
  const analytics = window ? window.analytics : undefined;
  if (analytics) {
    L.info('cognitoLogoutReducer()', 'Logging out and resetting analytics...');
    analytics.reset();
  }
  // analytics.reset() doesn't clear Intercom's cookie session, so shut it down explicitly.
  if (typeof window?.Intercom === 'function') {
    window.Intercom('shutdown');
  }
  upscopeUpdateConnection({ identities: null, uniqueId: null });
  return [{ eventType: EventTypes.reset }];
};

const trackCognitoLogin = (getState, action) => {
  const FTAG = 'trackCognitoLogin()';

  const { email, name, sub: id, phone_number: phone } = action?.attributes ?? {};

  if (!id || !email) {
    L.info(FTAG, 'Skipping identify, missing cognito sub or email:', { id, email });
    return;
  }

  // Always identify synchronously with full traits. Do NOT gate on the URL: a fresh
  // form login is at /#/login, not /#/salon/:id, so any URL gate corrupts contacts.
  // userId is the Cognito sub (UUID) - all legacy Intercom contacts are keyed on it,
  // so using email here orphans every pre-May-2026 contact and creates a duplicate.
  const baseTraits = { email, name, phone };
  L.info(FTAG, 'Identifying user:', id, 'with base traits (version follows once resolved)');
  SEGMENT.identify({ id, traits: baseTraits, options: { integrations: { All: false, Intercom: true } } });

  RELEASE.display()
    .then((version) => {
      const traits = { ...baseTraits, version };
      L.debug(FTAG, 'Re-identifying user:', id, 'with version trait:', traits);
      SEGMENT.identify({ id, traits, options: { integrations: { All: false, Intercom: true } } });
    })
    .catch((e) => L.warn(FTAG, e));

  upscopeUpdateConnection({ identities: [name], uniqueId: email });

  // Return nothing: redux-segment v1.6.2 treats a missing eventType as no-op (handleIndividualSpec
  // skips when !type), and the two SEGMENT.identify calls above already emit the events we need.
};

const TRACKER_MAP = {
  '@@router/LOCATION_CHANGE': trackLocationChange,
  POST_SALON_DATA_SUCCESS: trackPostSalonDataSuccess,
  COGNITO_LOGOUT: trackCognitoLogout,
  COGNITO_LOGIN: trackCognitoLogin,
};

const TRACKER_KEYS = fromObjectToKeys(TRACKER_MAP);

// noinspection JSCheckFunctionSignatures
const normalReduxSegmentTracker = createTracker({ mapper: TRACKER_MAP });

const strictReduxSegmentTracker = (store) => (next) => (action) => (TRACKER_KEYS.includes(action?.type) ? normalReduxSegmentTracker(store)(next)(action) : next(action));

const init = (conf) => {
  loadUpscope(conf);

  const analytics = window ? window.analytics : undefined;
  if (analytics && conf.segmentWriteKey) {
    // Load the real analytics.js library once at startup so subsequent identify/track calls
    // are sent immediately rather than queued in the stub. Calling this here (not inside
    // identify) prevents the stub's queued events from replaying on every identify call.
    if (typeof analytics.load === 'function') {
      L.debug('init()', 'Loading Segment analytics with key:', conf.segmentWriteKey);
      analytics.load(conf.segmentWriteKey);
    } else {
      L.warn('init()', 'analytics.load is not a function — library may already be loaded');
    }
  }

  if (USE_SEGMENT_AND_UPSCOPE) {
    // this is a kind of init done once on every page load
    trackPageLoad(conf);
  } else {
    L.debug('init()', 'USE_SEGMENT_AND_UPSCOPE:', USE_SEGMENT_AND_UPSCOPE, 'skipping trackPageLoad()');
  }
};

const SEGMENT = Object.freeze({
  identify,
  init,
  loadUpscope,
  reduxTracker: FLAG.trackBeCalls ? normalReduxSegmentTracker : strictReduxSegmentTracker,
  setCompany,
});

// noinspection JSUnusedGlobalSymbols
export default SEGMENT;
